import { useEffect, useState, useCallback } from "react";
import { useLocation } from "wouter";
import { useAppStore } from "../../store/app";
import { useT } from "../../lib/i18n/useT";
import { readAllVacancies, type Vacancy } from "../../lib/api/vacancies";
import { readAllWorkflows, type Workflow } from "../../lib/api/workflows";

function getDaysRemaining(deadlineStr: string): number {
  const now = new Date();
  const deadlineDate = new Date(deadlineStr);
  const diffTime = deadlineDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export default function Dashboard() {
  const t = useT();
  const repoPath = useAppStore((s) => s.repoPath);
  const lastLoginTime = useAppStore((s) => s.lastLoginTime);
  const [, navigate] = useLocation();
  const activeCandidateFolder = useAppStore((s) => s.activeCandidateFolder);

  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!repoPath || !activeCandidateFolder) return;
    
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [allVacancies, allWorkflows] = await Promise.all([
          readAllVacancies(repoPath),
          readAllWorkflows(repoPath, activeCandidateFolder),
        ]);
        if (active) {
          setVacancies(allVacancies);
          setWorkflows(allWorkflows);
        }
      } catch (err) {
         console.error("Error loading dashboard data", err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [repoPath, activeCandidateFolder]);

  // Urgency strip: active vacancies (not expired) with deadline <= 5 days
  const urgentVacancies = vacancies.filter((v) => {
    const earliestDeadline = v.routes.reduce((earliest, r) => {
      return !earliest || new Date(r.deadline) < new Date(earliest) ? r.deadline : earliest;
    }, "");
    
    if (!earliestDeadline) return false;
    const days = getDaysRemaining(earliestDeadline);
    return days >= 0 && days <= 5;
  });

  // Notifications: vacancies added since last login
  const newVacancies = vacancies.filter((v) => {
    if (!lastLoginTime) return false;
    return new Date(v.added_at) > new Date(lastLoginTime);
  });

  const notifyNewVacancies = useCallback(async (count: number) => {
    try {
      if (!("Notification" in window)) return;
      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }
      if (permission === "granted") {
        new Notification("Lens — Nieuwe vacature" + (count > 1 ? "s" : ""), {
          body: count === 1
            ? "Er is 1 nieuwe vacature beschikbaar in de bibliotheek."
            : `Er zijn ${count} nieuwe vacatures beschikbaar in de bibliotheek.`,
        });
      }
    } catch {
      // Notifications are best-effort
    }
  }, []);

  useEffect(() => {
    if (newVacancies.length > 0) {
      void notifyNewVacancies(newVacancies.length);
    }
    // Only fire once per load when new vacancies are first detected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newVacancies.length > 0]);

  // Active workflows lookup mapped with vacancy details
  const activeWorkflowItems = workflows
    .map((wf) => {
      const vacancy = vacancies.find((v) => v.id === wf.vacancy_id);
      
      const earliestDeadline = vacancy?.routes.reduce((earliest, r) => {
        return !earliest || new Date(r.deadline) < new Date(earliest) ? r.deadline : earliest;
      }, "") ?? "";
      
      const daysLeft = earliestDeadline ? getDaysRemaining(earliestDeadline) : null;
      
      return {
        workflow: wf,
        vacancy,
        deadline: earliestDeadline,
        daysLeft,
      };
    })
    // Sort workflows by urgency (earliest deadline first)
    .sort((a, b) => {
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
    });

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center bg-canvas">
        <div className="flex flex-col items-center gap-3">
          <span className="h-8 w-8 animate-spin rounded-full border-3 border-slate-300 border-t-slate-800" />
          <p className="text-sm font-medium text-slate-500">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

      {/* 1. Urgency Strip */}
      {urgentVacancies.length > 0 && (
        <div className="mb-8 overflow-hidden rounded-xl border border-amber-200 bg-amber-50/70 p-4 backdrop-blur-xs shadow-xs animate-pulse-slow">
          <div className="flex items-start gap-3">
            <span className="text-lg">⚠️</span>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-amber-800 uppercase tracking-wider">
                {t("vacancy.urgent")}
              </h4>
              <div className="mt-1 flex flex-wrap gap-x-6 gap-y-2 text-sm text-amber-700">
                {urgentVacancies.map((v) => {
                  const deadline = v.routes[0]?.deadline;
                  const days = deadline ? getDaysRemaining(deadline) : 0;
                  return (
                    <span key={v.id} className="font-medium">
                      <strong>{v.title}</strong> @ {v.client} ({days} {days === 1 ? "day" : "days"} left)
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid Layout */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        
        {/* Left/Middle: Workflows List (2 cols) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <h2 className="text-xl font-bold text-slate-800 tracking-tight">
              Active Workflows
            </h2>
            <button
              onClick={() => navigate("/vacancies")}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors hover:underline underline-offset-4 cursor-pointer focus:outline-none"
            >
              {t("nav.vacancies")} →
            </button>
          </div>

          {activeWorkflowItems.length === 0 ? (
            <div className="rounded-xl border border-slate-200/60 bg-white p-12 text-center shadow-xs">
              <p className="text-slate-400 text-sm">No active workflows currently.</p>
              <button
                onClick={() => navigate("/vacancies")}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 transition-colors shadow-xs cursor-pointer focus:outline-none"
              >
                Find Vacancies
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {activeWorkflowItems.map(({ workflow, vacancy, deadline, daysLeft }) => {
                const isUrgent = daysLeft !== null && daysLeft >= 0 && daysLeft <= 5;
                return (
                  <article
                    key={workflow.id}
                    className="flex flex-col justify-between rounded-xl border border-slate-200/60 bg-white p-5 shadow-xs hover:shadow-md hover:scale-[1.01] transition-all duration-200"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 truncate max-w-[120px]">
                          {vacancy?.client ?? "Unknown Client"}
                        </span>
                        
                        {/* Status Badge */}
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          workflow.status === "submitted" || workflow.status === "placed"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                            : workflow.status === "rejected"
                            ? "bg-red-50 text-red-700 border border-red-100"
                            : "bg-slate-100 text-slate-700 border border-slate-200/50"
                        }`}>
                          {t(`workflow.status.${workflow.status}` as any)}
                        </span>
                      </div>

                      <h3 className="mt-2 text-base font-bold text-slate-800 line-clamp-1">
                        {vacancy?.title ?? "Unknown Role"}
                      </h3>

                      {/* Match Score */}
                      {workflow.match_score && (
                        <div className="mt-3 flex items-center gap-1.5">
                          <span className={`h-2 w-2 rounded-full ${
                            workflow.match_score === "strong" ? "bg-emerald-500" :
                            workflow.match_score === "good" ? "bg-cyan-500" :
                            workflow.match_score === "partial" ? "bg-amber-500" : "bg-rose-500"
                          }`} />
                          <span className="text-xs font-semibold text-slate-500">
                            {t(`match.score.${workflow.match_score}` as any)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Footer Details */}
                    <div className="mt-6 border-t border-slate-50 pt-4 flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          {t("vacancy.deadline")}
                        </span>
                        <span className={`text-xs font-medium mt-0.5 ${isUrgent ? "text-rose-600 font-semibold" : "text-slate-600"}`}>
                          {deadline ? new Date(deadline).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "N/A"}
                          {isUrgent && " ⚠️"}
                        </span>
                      </div>

                      <button
                        onClick={() => navigate(`/workflows/${workflow.id}`)}
                        className="rounded-lg bg-slate-50 hover:bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 border border-slate-200/40 transition-colors cursor-pointer focus:outline-none"
                      >
                        Open Workspace
                      </button>
                    </div>

                  </article>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Panel: Notifications & Quick Actions (1 col) */}
        <div className="space-y-8">
          
          {/* Notifications Panel */}
          <section className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-800 tracking-tight border-b border-slate-100 pb-3">
              Notifications
            </h2>

            {newVacancies.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-xs text-slate-400">No new vacancies since last login.</p>
              </div>
            ) : (
              <div className="mt-4 divide-y divide-slate-100 max-h-[300px] overflow-y-auto pr-1">
                <p className="text-xs font-semibold text-indigo-600 mb-3">
                  {t(newVacancies.length === 1 ? 'notification.new_vacancy_since_one' : 'notification.new_vacancy_since_other', { count: newVacancies.length })}
                </p>
                {newVacancies.map((v) => (
                  <div key={v.id} className="py-3 flex flex-col hover:bg-slate-50/50 rounded-lg px-2 -mx-2 transition-colors">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{v.client}</span>
                    <span className="text-sm font-semibold text-slate-700 line-clamp-1 mt-0.5">{v.title}</span>
                    <button
                      onClick={() => navigate("/vacancies")}
                      className="text-left text-[10px] font-medium text-slate-500 hover:text-slate-700 underline mt-1 cursor-pointer focus:outline-none"
                    >
                      View in library
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Quick Actions Panel */}
          <section className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-800 tracking-tight border-b border-slate-100 pb-3">
              Quick Actions
            </h2>
            <div className="mt-4 flex flex-col gap-2.5">
              <button
                onClick={() => navigate("/vacancies")}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs cursor-pointer focus:outline-none text-left"
              >
                <span>Browse Vacancy Library</span>
                <span className="text-slate-400">→</span>
              </button>
              
              <button
                onClick={() => navigate("/vacancies/new")}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs cursor-pointer focus:outline-none text-left"
              >
                <div>
                  <span>Add Vacancy</span>
                  <p className="text-5xs text-slate-400 uppercase tracking-wider font-semibold mt-0.5">Vacancy capture</p>
                </div>
                <span className="text-slate-400">→</span>
              </button>
            </div>
          </section>

        </div>

      </div>

    </div>
  );
}
